import { useEffect, useState } from 'react';
import {
  AgxButton,
  AgxRow,
  AgxColumn,
  AgxBodyText,
} from '@urbanx/agx-ui-components';
import { useAppSelector } from 'hooks/useAppSelector';
import { cleanStreetAddress } from 'utils/formatAddress';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { ComparativeAddress } from '../ComparativeMarketAnalysis/types';
import { CMAPropertyResultPage } from './CMAPropertyResultPage';
import { CMASelectedProperties } from './CMASelectedProperties';
import './SearchNow.scss';

const MaxSelectedProperties = 5;
const SearchNowPage = {
  Filters: 'Filters',
  PropertyResults: 'PropertyResults',
  SelectedProperties: 'SelectedProperties',
};

interface SearchNowProps {
  id: string;
  onValueChanged: (value: { id: string; value: ComparativeAddress[] }) => void;
  defaultValue?: ComparativeAddress[];
}

const SearchNow = ({ id, onValueChanged, defaultValue }: SearchNowProps) => {
  const { PropertyAddress: propertyAddress } = useAppSelector(
    state => state.form.formValues
  );

  const { cmaPropertyResults } = useAppSelector(
    ({ cmaPropertySearch }) => cmaPropertySearch
  );
  const [searchNowPage, setSearchNowPage] = useState(
    (defaultValue?.length ?? 0) > 0
      ? SearchNowPage.SelectedProperties
      : SearchNowPage.Filters
  );

  const [selectedProperties, setSelectedProperties] = useState(
    defaultValue ?? []
  );

  const handleCMAPropertySearch = () => {
    setSearchNowPage(SearchNowPage.PropertyResults);
  };

  const handlePreviousPageClick = () => {
    setSearchNowPage(SearchNowPage.Filters);
  };

  useEffect(() => {
    const formattedAddresses = selectedProperties.map(property => {
      return {
        propertyAddress: property.propertyAddress,
        soldPrice: property.soldPrice,
        distanceInKilometers: property.distanceInKilometers,
        propertyDetails: {
          ...property.propertyDetails,
        },
      } as ComparativeAddress;
    });
    onValueChanged({ id, value: formattedAddresses });
  }, [selectedProperties]);

  if (searchNowPage === SearchNowPage.PropertyResults) {
    return (
      <div className="agx-fullPageOverlay">
        <CMAPropertyResultPage
          onPreviousPageClick={handlePreviousPageClick}
          onShowSelectedProperties={selectedProperties => {
            setSelectedProperties(selectedProperties);
            setSearchNowPage(SearchNowPage.SelectedProperties);
          }}
          maxSelectedProperties={MaxSelectedProperties}
        />
      </div>
    );
  } else if (searchNowPage === SearchNowPage.SelectedProperties) {
    return (
      <CMASelectedProperties
        onEditSelectedProperties={() => {
          setSelectedProperties([]);

          if ((cmaPropertyResults?.length ?? 0) > 0) {
            setSearchNowPage(SearchNowPage.PropertyResults);
          } else {
            setSearchNowPage(SearchNowPage.Filters);
          }
        }}
        selectedProperties={selectedProperties}
      />
    );
  } else {
    return (
      <AgxColumn veryLargeGap fill>
        <AgxBodyText medium>
          Automatically find comparable properties near{' '}
          <b>{cleanStreetAddress(propertyAddress)}</b>
        </AgxBodyText>
        <AgxRow>
          <AgxButton
            dataTestId="agx-findComparables-button"
            text="Find Comparables"
            primary
            large
            onClick={handleCMAPropertySearch}
            wide={ScreenSize() === Breakpoints.Mobile}
          />
        </AgxRow>
      </AgxColumn>
    );
  }
};

export default SearchNow;
